import { useState, useMemo } from 'react';
import { demoCanteenItems, demoCanteenBills, demoRooms } from '@/mocks/demoData';
import type { CanteenItem, CanteenBill, Booking } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { generateKOTPDF, generateCanteenBillPDF } from '@/utils/pdfUtils';
import { getCheckedInBookings, increasePendingAmount } from '@/utils/bookingStore';
import { addActivityLog } from '@/utils/activityLogStore';
import { addShiftCollectionFromCanteen } from '@/utils/shiftStore';

export default function CanteenBilling() {
  const { addToast } = useApp();
  const [items, setItems] = useState<CanteenItem[]>(demoCanteenItems);
  const [cart, setCart] = useState<{ item: CanteenItem; qty: number }[]>([]);
  const [tableOrRoom, setTableOrRoom] = useState('');
  const [discount, setDiscount] = useState('');
  const [discountType, setDiscountType] = useState<'percent' | 'flat'>('flat');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [showKOT, setShowKOT] = useState(false);
  const [generatedBill, setGeneratedBill] = useState<CanteenBill | null>(null);
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = useMemo(() => [...new Set(items.map((i) => i.category))], [items]);

  const filteredItems = useMemo(() => {
    return items.filter((i) => i.available && (activeCategory === 'all' || i.category === activeCategory));
  }, [items, activeCategory]);

  const addToCart = (item: CanteenItem) => {
    setCart((prev) => {
      const existing = prev.find((c) => c.item.id === item.id);
      if (existing) return prev.map((c) => c.item.id === item.id ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { item, qty: 1 }];
    });
  };

  const updateQty = (itemId: string, qty: number) => {
    if (qty <= 0) setCart((prev) => prev.filter((c) => c.item.id !== itemId));
    else setCart((prev) => prev.map((c) => c.item.id === itemId ? { ...c, qty } : c));
  };

  const subtotal = cart.reduce((s, c) => s + c.item.price * c.qty, 0);
  const gstTotal = cart.reduce((s, c) => s + (c.item.price * c.qty * c.item.gst_percent / 100), 0);
  const discountValue = discountType === 'percent' ? subtotal * (Number(discount) || 0) / 100 : Number(discount) || 0;
  const total = Math.max(0, subtotal + gstTotal - discountValue);

  const handleGenerateKOT = () => {
    if (cart.length === 0) { addToast('error', 'Add items to cart first'); return; }
    if (!tableOrRoom) { addToast('error', 'Enter table or room number'); return; }
    setShowKOT(true);
    addToast('success', 'KOT generated successfully');
  };

  const handlePrintKOT = () => {
    generateKOTPDF(
      tableOrRoom,
      cart.map((c) => ({ name: c.item.name, qty: c.qty, price: c.item.price }))
    );
    addToast('success', 'KOT PDF downloaded');
  };

  const handleBill = () => {
    if (cart.length === 0) { addToast('error', 'Cart is empty'); return; }
    if (!tableOrRoom) { addToast('error', 'Enter table or room number'); return; }
    const newBill: CanteenBill = {
      id: `cb-${Date.now()}`,
      bill_no: `CNT-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${String(demoCanteenBills.length + 1).padStart(3,'0')}`,
      table_or_room: tableOrRoom,
      items: cart.map((c) => ({ item_id: c.item.id, name: c.item.name, qty: c.qty, price: c.item.price, gst: c.item.price * c.qty * c.item.gst_percent / 100 })),
      subtotal,
      gst_total: gstTotal,
      discount: Number(discount) || 0,
      discount_type: discountType,
      total,
      payment_method: paymentMethod,
      status: paymentMethod === 'room_charge' ? 'pending' : 'paid',
      created_at: new Date().toISOString(),
    };
    setGeneratedBill(newBill);
    if (paymentMethod === 'room_charge') {
      const roomNum = tableOrRoom.replace(/room\s*/i, '').trim();
      const room = demoRooms.find((r) => r.room_number === roomNum);
      if (room) {
        const bookings = getCheckedInBookings();
        const booking = bookings.find((b: Booking) => b.room_id === room.id);
        if (booking) {
          increasePendingAmount(booking.id, total);
          addActivityLog({
            user_id: 'usr-003',
            user_name: 'Reception',
            action: 'Canteen Room Charge',
            entity_type: 'canteen_bill',
            entity_id: newBill.id,
            details: { total, room: tableOrRoom },
          });
          addToast('success', `Room charge Rs. ${total.toLocaleString('en-IN')} added to ${tableOrRoom} bill`);
        } else {
          addToast('warning', `No active booking found for ${tableOrRoom}. Bill saved as pending.`);
        }
      } else {
        addToast('warning', `Room ${tableOrRoom} not found. Bill saved as pending.`);
      }
    } else {
      addShiftCollectionFromCanteen(total, paymentMethod);
      addActivityLog({
        user_id: 'usr-003',
        user_name: 'Reception',
        action: 'Canteen Bill',
        entity_type: 'canteen_bill',
        entity_id: newBill.id,
        details: { total, method: paymentMethod, table: tableOrRoom },
      });
      addToast('success', `Bill ${newBill.bill_no} generated — Rs. ${total.toLocaleString('en-IN')} added to shift`);
    }
    setCart([]); setTableOrRoom(''); setDiscount(''); setPaymentMethod('cash'); setShowKOT(false);
  };

  const handlePrintGeneratedBill = () => {
    if (!generatedBill) return;
    generateCanteenBillPDF(generatedBill);
    addToast('success', 'Bill PDF downloaded');
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Canteen Billing & KOT</h1>
        <p className="text-navy-500 mt-1">Table billing, KOT generation, and payment processing.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Menu */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-white rounded-xl border border-navy-100 p-4">
            <div className="flex gap-2 overflow-x-auto pb-2">
              <button onClick={() => setActiveCategory('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeCategory === 'all' ? 'bg-navy-700 text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}>All</button>
              {categories.map((c) => (
                <button key={c} onClick={() => setActiveCategory(c)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeCategory === c ? 'bg-navy-700 text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}>{c}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {filteredItems.map((item) => (
              <button key={item.id} onClick={() => addToCart(item)} className="text-left bg-white rounded-xl border border-navy-100 p-4 hover:border-gold-400 hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-xs font-medium text-navy-400">{item.category}</span>
                  <i className="ri-add-circle-line text-gold-500 text-lg" />
                </div>
                <div className="font-semibold text-navy-800 text-sm mb-1">{item.name}</div>
                <div className="text-lg font-bold text-navy-700">Rs. {item.price.toLocaleString('en-IN')}</div>
                <div className="text-xs text-navy-400">+GST {item.gst_percent}%</div>
              </button>
            ))}
          </div>
        </div>

        {/* Cart & Bill */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-navy-100 p-5">
            <h3 className="font-semibold text-navy-800 mb-3">Table / Room</h3>
            <input type="text" placeholder="e.g. Table 3 or Room 101" value={tableOrRoom} onChange={(e) => setTableOrRoom(e.target.value)} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>

          <div className="bg-white rounded-xl border border-navy-100 p-5">
            <h3 className="font-semibold text-navy-800 mb-3">Cart ({cart.reduce((s, c) => s + c.qty, 0)} items)</h3>
            {cart.length === 0 ? (
              <div className="text-center py-6 text-navy-400 text-sm">Tap menu items to add to cart</div>
            ) : (
              <div className="space-y-3 mb-4">
                {cart.map((c) => (
                  <div key={c.item.id} className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-medium text-navy-800">{c.item.name}</div>
                      <div className="text-xs text-navy-500">Rs. {c.item.price.toLocaleString('en-IN')} x {c.qty}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => updateQty(c.item.id, c.qty - 1)} className="w-7 h-7 rounded-lg bg-navy-50 text-navy-600 flex items-center justify-center hover:bg-navy-100 transition-colors cursor-pointer"><i className="ri-subtract-line" /></button>
                      <span className="w-6 text-center text-sm font-medium">{c.qty}</span>
                      <button onClick={() => updateQty(c.item.id, c.qty + 1)} className="w-7 h-7 rounded-lg bg-navy-50 text-navy-600 flex items-center justify-center hover:bg-navy-100 transition-colors cursor-pointer"><i className="ri-add-line" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {cart.length > 0 && (
              <>
                <div className="border-t border-navy-100 pt-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-navy-500">Subtotal</span><span className="font-medium text-navy-800">Rs. {subtotal.toLocaleString('en-IN')}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-navy-500">GST Total</span><span className="font-medium text-navy-800">Rs. {gstTotal.toLocaleString('en-IN')}</span></div>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-navy-500 mb-1">Discount</label>
                      <input type="number" placeholder="0" value={discount} onChange={(e) => setDiscount(e.target.value)} className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
                    </div>
                    <select value={discountType} onChange={(e) => setDiscountType(e.target.value as 'percent' | 'flat')} className="px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500">
                      <option value="flat">Rs.</option>
                      <option value="percent">%</option>
                    </select>
                  </div>
                  <div className="flex justify-between text-lg font-bold pt-2 border-t border-navy-100">
                    <span className="text-navy-800">Total</span>
                    <span className="text-navy-800">Rs. {total.toLocaleString('en-IN')}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-xs font-medium text-navy-500 mb-1.5">Payment Method</label>
                  <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 mb-3">
                    <option value="cash">Cash</option>
                    <option value="card">Card</option>
                    <option value="upi">UPI</option>
                    <option value="room_charge">Room Charge</option>
                  </select>
                </div>

                <div className="flex gap-3 mt-2">
                  <button onClick={handleGenerateKOT} className="flex-1 px-4 py-2.5 bg-navy-100 text-navy-700 rounded-lg text-sm font-medium hover:bg-navy-200 transition-colors cursor-pointer">
                    <i className="ri-file-list-line mr-1" /> KOT
                  </button>
                  <button onClick={handleBill} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">
                    <i className="ri-bill-line mr-1" /> Generate Bill
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* KOT Preview */}
      {showKOT && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Kitchen Order Token</h3>
              <button onClick={() => setShowKOT(false)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 text-center">
              <div className="text-2xl font-bold text-navy-800 mb-1">KOT</div>
              <div className="text-sm text-navy-500 mb-4">{tableOrRoom} | {new Date().toLocaleString('en-IN')}</div>
              <div className="space-y-2 text-left mb-4">
                {cart.map((c) => (
                  <div key={c.item.id} className="flex justify-between text-sm">
                    <span className="text-navy-800">{c.qty}x {c.item.name}</span>
                    <span className="font-medium text-navy-700">Rs. {(c.item.price * c.qty).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-navy-100 pt-3">
                <div className="text-lg font-bold text-navy-800">{cart.reduce((s, c) => s + c.qty, 0)} Items</div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={handlePrintKOT} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">
                <i className="ri-file-download-line mr-2" /> Download KOT PDF
              </button>
              <button onClick={() => setShowKOT(false)} className="px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer whitespace-nowrap">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Preview */}
      {generatedBill && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">Bill Generated</h3>
              <button onClick={() => setGeneratedBill(null)} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6">
              <div className="text-center mb-4">
                <div className="text-xl font-bold text-navy-800">{generatedBill.bill_no}</div>
                <div className="text-sm text-navy-500">{generatedBill.table_or_room}</div>
              </div>
              <div className="space-y-2 mb-4">
                {generatedBill.items.map((it, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-navy-800">{it.qty}x {it.name}</span>
                    <span className="font-medium">Rs. {(it.price * it.qty).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-navy-100 pt-3 space-y-1">
                <div className="flex justify-between text-sm"><span className="text-navy-500">Subtotal</span><span>Rs. {generatedBill.subtotal.toLocaleString('en-IN')}</span></div>
                <div className="flex justify-between text-sm"><span className="text-navy-500">GST</span><span>Rs. {generatedBill.gst_total.toLocaleString('en-IN')}</span></div>
                {generatedBill.discount > 0 && <div className="flex justify-between text-sm"><span className="text-navy-500">Discount</span><span className="text-green-600">-Rs. {(generatedBill.discount_type === 'percent' ? generatedBill.subtotal * generatedBill.discount / 100 : generatedBill.discount).toLocaleString('en-IN')}</span></div>}
                <div className="flex justify-between text-lg font-bold pt-2"><span className="text-navy-800">Total</span><span className="text-navy-800">Rs. {generatedBill.total.toLocaleString('en-IN')}</span></div>
              </div>
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                <div className="text-sm text-green-700 font-medium capitalize">{generatedBill.payment_method.replace('_', ' ')} — {generatedBill.status}</div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={handlePrintGeneratedBill} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">
                <i className="ri-file-download-line mr-2" /> Download Bill PDF
              </button>
              <button onClick={() => setGeneratedBill(null)} className="px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer whitespace-nowrap">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}